import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const sizeMap = {
  small: 16,
  medium: 20,
};

const StyledIcon = styled.svg.attrs(({ size }) => ({
  viewBox: `0 0 ${sizeMap[size]} ${sizeMap[size]}`,
  height: sizeMap[size],
  width: sizeMap[size],
}))`
  height: ${({ size }) => sizeMap[size]}px;
  width: ${({ size }) => sizeMap[size]}px;

  ${({ foregroundColor }) =>
    foregroundColor &&
    `
    path {
      fill: ${foregroundColor};
    }
  `}

  ${({ backgroundColor }) =>
    backgroundColor &&
    `
    circle,
    rect {
      fill: ${backgroundColor};
    }
  `}
`;

/**
 * Icon component deals with artwork selection very manually for now, but in the future if we want
 * to get our SVG game on point this could be a map of well defined foreground & background paths.
 */
const Icon = ({ type, ...iconProps }) => (
  <StyledIcon {...iconProps}>
    {type === 'calendar' && (
      <path d="M15.294.25c.38 0 .694.282.743.648l.007.102v1.367H17c1.463 0 2.658 1.142 2.745 2.583l.005.168V17c0 1.519-1.231 2.75-2.75 2.75H3C1.481 19.75.25 18.519.25 17V5.118c0-1.52 1.231-2.75 2.75-2.75l.955-.001V1c0-.414.337-.75.75-.75.38 0 .694.282.744.648L5.456 1l-.001 1.367h9.089V1c0-.414.336-.75.75-.75zm2.956 9.058H1.75V17c0 .647.492 1.18 1.122 1.244L3 18.25h14c.69 0 1.25-.56 1.25-1.25V9.308zM3.955 3.867H3c-.69 0-1.25.56-1.25 1.25v2.691h16.5v-2.69c0-.648-.492-1.18-1.122-1.244L17 3.868l-.956-.001v2.045c0 .414-.336.75-.75.75-.38 0-.693-.283-.743-.649l-.007-.101V3.867H5.455v2.045c0 .414-.335.75-.75.75-.379 0-.693-.283-.742-.649l-.007-.101-.001-2.045z" />
    )}
    {type === 'close' && (
      <g fill="none">
        <circle cx="10" cy="10" r="10" fill="#DDD" opacity=".505" />
        <path
          fill="#2EC4B6"
          d="M13.792 6.208c.252.253.275.649.068.927l-.068.08L11.007 10l2.785 2.785c.277.278.277.729 0 1.007-.253.252-.649.275-.927.068l-.08-.068L10 11.007l-2.785 2.785c-.278.277-.729.277-1.007 0-.252-.253-.275-.649-.068-.927l.068-.08L8.993 10 6.208 7.215c-.277-.278-.277-.729 0-1.007.253-.252.649-.275.927-.068l.08.068L10 8.993l2.785-2.785c.278-.277.729-.277 1.007 0z"
        />
      </g>
    )}
    {type === 'down-caret' && (
      <path
        fill="#2EC4B6"
        d="M18.743 5.197c.306-.28.78-.26 1.06.046.254.278.26.695.03.979l-.076.08-9.25 8.48c-.258.237-.64.26-.924.071l-.09-.071-9.25-8.48c-.305-.279-.326-.753-.046-1.059.255-.277.67-.32.973-.115l.087.07L10 13.211l8.743-8.015z"
      />
    )}
    {type === 'left-caret' && (
      <path
        fill="#2EC4B6"
        d="M14.798 18.711c.287.313.266.8-.047 1.087-.285.26-.713.267-1.004.03l-.083-.077-8.462-9.231c-.242-.265-.267-.657-.073-.947l.073-.093L13.664.25c.287-.314.773-.335 1.087-.048.284.261.328.687.117.998l-.07.089L6.812 10l7.986 8.711z"
      />
    )}
    {type === 'right-caret' && (
      <path
        fill="#2EC4B6"
        d="M5.202 1.289c-.287-.313-.266-.8.047-1.087.285-.26.713-.267 1.004-.03l.083.077 8.462 9.231c.242.265.267.657.073.947l-.073.093-8.462 9.23c-.287.314-.773.335-1.087.048-.284-.261-.328-.687-.117-.998l.07-.089L13.188 10 5.202 1.289z"
      />
    )}
    {type === 'error' && (
      <path
        fill="#F95541"
        d="M14.788 5.212c.26.26.28.668.064.952l-.064.074L11.026 10l3.762 3.762c.283.283.283.742 0 1.026-.26.26-.668.28-.952.064l-.074-.064L10 11.026l-3.762 3.762c-.283.283-.742.283-1.026 0-.26-.26-.28-.668-.064-.952l.064-.074L8.974 10 5.212 6.238c-.283-.283-.283-.742 0-1.026.26-.26.668-.28.952-.064l.074.064L10 8.974l3.762-3.762c.283-.283.742-.283 1.026 0z"
      />
    )}
    {type === 'complete' && (
      <g fill="none">
        <circle cx="10" cy="10" r="10" fill="#39CB7B" />
        <path
          fill="#FFF"
          d="M13.497 6.282c.26-.324.731-.376 1.055-.118.294.235.364.647.18.963l-.063.091-5.329 6.667c-.246.308-.682.369-1 .157l-.084-.065-3.004-2.67c-.31-.276-.338-.75-.063-1.06.25-.28.665-.33.971-.13l.088.069 2.414 2.144 4.835-6.048z"
        />
      </g>
    )}
    {type === 'complete-unfilled' && (
      <g fill="none">
        <path
          fill="#2EC4B6"
          d="M11.2.85c5.716 0 10.35 4.634 10.35 10.35 0 5.716-4.634 10.35-10.35 10.35C5.484 21.55.85 16.916.85 11.2.85 5.484 5.484.85 11.2.85zm0 1.5c-4.887 0-8.85 3.963-8.85 8.85s3.963 8.85 8.85 8.85 8.85-3.963 8.85-8.85-3.963-8.85-8.85-8.85zm5.09 5.217c.27.263.3.679.085.975l-.071.085-6.038 6.192c-.27.277-.7.3-.995.07l-.085-.076-3.096-3.25c-.286-.3-.274-.775.026-1.06.273-.26.69-.274.978-.05l.082.075 2.559 2.687L15.23 7.58c.289-.296.764-.302 1.06-.013z"
        />
      </g>
    )}
    {type === 'incomplete' && (
      <g fill="none">
        <circle cx="10" cy="10" r="10" fill="#F95541" />
        <path
          fill="#FFF"
          d="M13.792 6.208c.252.253.275.649.068.927l-.068.08L11.007 10l2.785 2.785c.277.278.277.729 0 1.007-.253.252-.649.275-.927.068l-.08-.068L10 11.007l-2.785 2.785c-.278.277-.729.277-1.007 0-.252-.253-.275-.649-.068-.927l.068-.08L8.993 10 6.208 7.215c-.277-.278-.277-.729 0-1.007.253-.252.649-.275.927-.068l.08.068L10 8.993l2.785-2.785c.278-.277.729-.277 1.007 0z"
        />
      </g>
    )}
    {type === 'success' && (
      <path
        fill="#39CB7B"
        d="M13.664 6.272c.259-.312.73-.363 1.054-.114.294.227.365.623.18.928l-.062.088L9.4 13.728c-.246.297-.682.356-1 .152l-.084-.063-3.064-2.625c-.31-.265-.338-.722-.063-1.02.25-.272.665-.319.971-.126l.088.065 2.473 2.12 4.943-5.96z"
      />
    )}
    {type === 'info' && (
      <g fill="none">
        <circle cx="10" cy="10" r="10" fill="#2EC4B6" />
        <path
          fill="#FFF"
          d="M10 7c.38 0 .693.282.743.648l.007.102v8c0 .414-.336.75-.75.75-.38 0-.693-.282-.743-.648l-.007-.102v-8c0-.414.336-.75.75-.75zm0-3c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1z"
        />
      </g>
    )}
    {type === 'warning' && (
      <g fill="none">
        <path
          fill="#F9C107"
          d="M9.362 5.66c.505-.88 1.77-.88 2.275 0l2.094 3.641 2.092 3.641c.505.88-.128 1.98-1.139 1.98H6.316c-1.011 0-1.644-1.1-1.138-1.98L7.27 9.301 9.362 5.66z"
        />
        <path
          fill="#FFF"
          d="M10.5 12c.276 0 .5.224.5.5s-.224.5-.5.5-.5-.224-.5-.5.224-.5.5-.5zm0-4c.245 0 .45.177.492.41L11 8.5v2c0 .276-.224.5-.5.5-.245 0-.45-.177-.492-.41L10 10.5v-2c0-.276.224-.5.5-.5z"
        />
      </g>
    )}
    {type === 'checkbox-filled' && (
      <g fill="none">
        <rect width="20" height="20" fill="#2EC4B6" rx="3" />
        <path
          fill="#FFF"
          d="M13.664 6.272c.259-.312.73-.363 1.054-.114.294.227.365.623.18.928l-.062.088L9.4 13.728c-.246.297-.682.356-1 .152l-.084-.063-3.064-2.625c-.31-.265-.338-.722-.063-1.02.25-.272.665-.319.971-.126l.088.065 2.473 2.12 4.943-5.96z"
        />
      </g>
    )}
    {type === 'checkbox-unfilled' && (
      <rect width="20" height="20" fill="#FFF" stroke="#DDD" rx="3" />
    )}
    {type === 'checkbox-simple' && (
      <g fill="none">
        <circle cx="10" cy="10" r="10" fill="#2EC4B6" />
        <path
          fill="#FFF"
          d="M10 7c.38 0 .693.282.743.648l.007.102v8c0 .414-.336.75-.75.75-.38 0-.693-.282-.743-.648l-.007-.102v-8c0-.414.336-.75.75-.75zm0-3c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1z"
        />
      </g>
    )}
    {type === 'add' && (
      <path
        fill="#2EC4B6"
        d="M10 0c5.523 0 10 4.477 10 10s-4.477 10-10 10S0 15.523 0 10 4.477 0 10 0zm0 1.463c-4.715 0-8.537 3.822-8.537 8.537 0 4.715 3.822 8.537 8.537 8.537 4.715 0 8.537-3.822 8.537-8.537 0-4.715-3.822-8.537-8.537-8.537zM10 6c.338 0 .616.25.66.576l.007.09-.001 2.667h2.667c.369 0 .667.299.667.667 0 .338-.25.616-.576.66l-.09.007-2.668-.001v2.667c0 .369-.298.667-.666.667-.338 0-.616-.25-.66-.576l-.007-.09v-2.668H6.667C6.298 10.667 6 10.369 6 10c0-.338.25-.616.576-.66l.09-.007h2.667V6.667c0-.369.299-.667.667-.667z"
      />
    )}
    {type === 'pencil' && (
      <path
        fill="#2EC4B6"
        d="M18.503 4.45l-1.682 1.682-2.967-2.969 1.683-1.684c.028-.028.063-.032.081-.032l2.884 2.835c.047.047.047.122.001.168zM6.526 16.427l-4.592 1.625 1.625-4.594 9.26-9.26 2.968 2.968-9.261 9.261zM19.538 3.248l-2.803-2.8c-.596-.599-1.643-.597-2.234 0l-2.196 2.195-.003.002-.002.003-9.894 9.893c-.076.077-.135.17-.172.273L.042 19.01c-.095.265-.026.561.173.76.139.141.326.215.517.215.082 0 .165-.014.244-.042l6.195-2.192c.103-.036.196-.096.273-.173L19.538 5.484c.615-.615.616-1.619 0-2.236z"
      />
    )}
    {type === 'hide' && (
      <path
        fill="#2EC4B6"
        d="M19.831 1.251c.229.275.222.676 0 .942l-.074.076-4.039 3.594c1.503.977 2.89 2.27 4.123 3.71.212.248.212.606 0 .854C17.166 13.562 13.75 16 10 16c-1.538 0-3.02-.412-4.42-1.115l-4.427 3.94c-.292.26-.733.226-.984-.076-.229-.275-.222-.676 0-.942l.074-.076 4.038-3.594c-1.501-.977-2.889-2.27-4.122-3.71-.212-.248-.212-.606 0-.854C2.839 6.435 6.25 4 10 4c1.537 0 3.02.412 4.42 1.114l4.427-3.94c.292-.26.733-.225.984.077zm-5.178 5.56L13.07 8.22c.326.52.513 1.129.513 1.78 0 1.903-1.607 3.451-3.584 3.451-.777 0-1.496-.239-2.084-.645L6.688 13.9c.89.396 1.802.653 2.722.73l.3.02.29.006c2.906 0 5.758-1.845 8.076-4.314l.238-.258.074-.084-.074-.083c-1.11-1.22-2.35-2.301-3.661-3.106zM10 5.344c-2.906 0-5.754 1.842-8.075 4.314l-.239.258-.075.083.307.333c1.05 1.118 2.208 2.108 3.43 2.857L6.93 11.78c-.326-.52-.514-1.13-.514-1.78 0-1.904 1.608-3.453 3.584-3.453.777 0 1.497.24 2.085.646L13.312 6.1c-.886-.395-1.795-.65-2.712-.73l-.31-.02-.29-.006zm2.01 3.82l-3.033 2.698c.306.156.654.245 1.023.245 1.206 0 2.189-.946 2.189-2.107 0-.297-.064-.58-.18-.836zM10 7.891c-1.206 0-2.189.947-2.189 2.109 0 .297.064.58.18.836l3.033-2.699c-.306-.157-.654-.246-1.024-.246z"
      />
    )}
    {type === 'show' && (
      <path
        fill="#2EC4B6"
        d="M10 4c3.74 0 7.16 2.438 9.84 5.573.213.248.213.606.001.854C17.166 13.562 13.75 16 10 16c-3.74 0-7.155-2.436-9.84-5.573-.213-.248-.213-.606-.001-.854C2.839 6.435 6.25 4 10 4zm0 1.344c-3.006 0-5.95 1.972-8.314 4.572l-.075.083.307.333c2.249 2.393 4.991 4.2 7.792 4.318l.29.006c3.006 0 5.955-1.974 8.314-4.572l.074-.084-.074-.083c-2.289-2.514-5.126-4.445-8.024-4.567zm0 1.203c1.977 0 3.584 1.549 3.584 3.453 0 1.903-1.607 3.451-3.584 3.451S6.416 11.903 6.416 10c0-1.904 1.608-3.453 3.584-3.453zm0 1.344c-1.206 0-2.189.947-2.189 2.109 0 1.16.983 2.107 2.189 2.107s2.189-.946 2.189-2.107c0-1.162-.983-2.109-2.189-2.109z"
      />
    )}
    {type === 'search' && (
      <path
        fill="#2EC4B6"
        d="M12.466 0c4.125 0 7.466 3.339 7.466 7.453 0 2.595-1.33 4.882-3.345 6.217l3.295 5.17c.225.354.118.82-.238 1.043-.325.203-.744.134-.987-.146l-.067-.09-3.358-5.269c-.855.341-1.789.529-2.766.529C8.34 14.907 5 11.568 5 7.453 5 3.34 8.34 0 12.466 0zm0 1.512c-3.278 0-5.938 2.658-5.938 5.941s2.66 5.942 5.938 5.942c3.278 0 5.938-2.659 5.938-5.942 0-3.283-2.66-5.941-5.938-5.941z"
      />
    )}
    {type === 'fax' && (
      <path
        fill="#2EC4B6"
        d="M16.063 0c.552 0 1 .449 1 1.004v4.848C18.138 5.887 19 6.767 19 7.854v10.137C19 19.1 18.104 20 17 20H3c-1.104 0-2-.9-2-2.009V6.808C1 5.699 1.895 4.8 3 4.8h1.209c.755 0 1.405.425 1.745 1.045h.562V1.004c0-.555.447-1.004 1-1.004zM17 7.352H6.209v10.639c0 .175-.029.341-.07.502H17c.275 0 .5-.225.5-.502V7.854c0-.276-.225-.502-.5-.502zM4.209 6.306H3c-.276 0-.5.225-.5.502v11.183c0 .277.224.502.5.502h1.21c.275 0 .499-.226.499-.502V6.808c0-.277-.224-.502-.5-.502zm5.048 10.22l.104.009c.168.031.308.138.396.28.057.092.098.194.098.311v.097c0 .083-.016.162-.047.234-.09.215-.303.367-.55.367H8.105c-.165 0-.315-.068-.423-.176-.108-.108-.175-.259-.175-.425v-.097c0-.166.067-.316.175-.425.108-.109.258-.176.423-.176h1.151zm3.108 0c.165 0 .315.066.423.175.108.11.175.26.175.425v.097c0 .166-.067.317-.175.425-.108.108-.258.176-.423.176h-1.151c-.165 0-.314-.068-.423-.176-.108-.108-.175-.259-.175-.425v-.097c0-.166.067-.316.175-.425.054-.054.119-.098.19-.129.072-.03.15-.047.233-.047zm3.107 0c.166 0 .315.066.423.175.108.11.175.26.175.425v.097c0 .332-.267.601-.598.601h-1.15c-.166 0-.315-.068-.424-.176-.108-.108-.175-.259-.175-.425v-.097c0-.166.067-.316.175-.425.054-.054.12-.098.19-.129.072-.03.151-.047.233-.047zm-6.214-2.063c.132 0 .247.05.346.124.087.063.163.142.205.242.03.072.046.152.046.235v.097c0 .083-.017.16-.046.234-.091.215-.304.367-.551.367H8.107c-.165 0-.315-.068-.423-.176-.109-.11-.176-.26-.176-.425v-.097c0-.332.268-.601.599-.601zm3.107 0c.165 0 .315.067.423.176.108.108.175.259.175.425v.097c0 .165-.067.316-.175.425-.108.108-.258.176-.423.176h-1.151c-.165 0-.314-.068-.423-.176-.108-.11-.175-.26-.175-.425v-.097c0-.166.067-.317.175-.425.108-.11.258-.176.423-.176zm3.107 0c.166 0 .315.067.423.176.108.108.175.259.175.425v.097c0 .33-.267.6-.598.6h-1.15c-.166 0-.315-.067-.424-.175-.108-.11-.175-.26-.175-.425v-.097c0-.166.067-.317.175-.425.11-.11.258-.176.423-.176zm-6.214-2.064c.132 0 .247.052.346.124.087.064.163.143.205.243.03.072.046.152.046.235v.096c0 .332-.268.601-.598.601h-1.15c-.083 0-.161-.017-.233-.047-.12-.05-.212-.146-.276-.258-.053-.088-.09-.185-.09-.296v-.096c0-.332.268-.602.599-.602zm3.107 0c.165 0 .315.067.423.177.108.108.175.26.175.425v.096c0 .25-.15.463-.365.554-.072.03-.15.047-.233.047h-1.151c-.082 0-.161-.017-.233-.047-.215-.09-.365-.305-.365-.554v-.096c0-.166.067-.317.175-.425.108-.11.258-.177.423-.177zm3.107 0c.166 0 .315.067.423.177.108.108.175.26.175.425v.096c0 .25-.15.463-.365.554-.07.03-.15.047-.233.047h-1.15c-.083 0-.162-.017-.234-.047-.214-.09-.365-.305-.365-.554v-.096c0-.166.067-.317.175-.425.11-.11.258-.177.423-.177zm.098-3.928c.276 0 .5.224.5.502v2.032c0 .277-.224.503-.5.503H8.008c-.276 0-.5-.226-.5-.503V8.973c0-.278.224-.502.5-.502zm-.007-6.965H8.016v4.34h7.547v-4.34zm-1.791 1.55c.414 0 .75.337.75.753s-.336.753-.75.753H9.807c-.414 0-.75-.337-.75-.753s.336-.753.75-.753z"
      />
    )}
    {type === 'printer' && (
      <path
        fill="#2EC4B6"
        d="M14.843 0c.765 0 1.388.623 1.388 1.388v4.77h1.273c.825 0 1.496.672 1.496 1.497v7.083c0 .825-.671 1.497-1.496 1.497h-1.273v2.149c0 .89-.724 1.616-1.615 1.616H5.385c-.891 0-1.615-.726-1.615-1.616v-2.149H2.496c-.824 0-1.496-.672-1.496-1.497V7.655c0-.825.672-1.497 1.496-1.497H3.77v-4.77C3.77.623 4.392 0 5.158 0zm-.227 12.105H5.385c-.127 0-.231.104-.231.232v6.047c0 .126.104.23.231.23h9.231c.127 0 .23-.104.23-.23v-6.047c0-.128-.103-.232-.23-.232zm-1.846 4.156c.382 0 .692.311.692.692 0 .383-.31.693-.692.693H7.23c-.381 0-.691-.31-.691-.693 0-.38.31-.692.692-.692zm0-2.285c.382 0 .692.31.692.692 0 .383-.31.693-.692.693H7.23c-.381 0-.691-.31-.691-.693 0-.382.31-.692.692-.692zm4.734-6.433H2.496c-.061 0-.111.05-.111.112v7.083c0 .062.05.112.111.112H3.77v-2.513c0-.892.724-1.617 1.615-1.617h9.231c.891 0 1.615.725 1.615 1.617v2.513h1.273c.062 0 .112-.05.112-.112V7.655c0-.062-.05-.112-.112-.112zM5.158 1.384l-.003 4.774h9.691v-4.77l-9.688-.004z"
      />
    )}
    {type === 'envelope' && (
      <path
        fill="#2EC4B6"
        d="M14.781 9.541l3.755-3.256v7.513l-3.755-4.257zm-8.717.85l2.569 2.11c.387.314.873.473 1.361.473.493 0 .99-.162 1.39-.489l2.314-2.005 3.638 4.124H2.348l3.716-4.213zm4.354 1.047c-.23.189-.606.187-.833.002l-7.36-6.045h15.163l-6.97 6.043zM1.463 6.611L4.955 9.48l-3.492 3.959V6.611zM18.293 4H1.707C.766 4 0 4.73 0 5.628v8.744C0 15.27.766 16 1.707 16h16.586c.941 0 1.707-.73 1.707-1.628V5.628C20 4.73 19.234 4 18.293 4z"
      />
    )}
    {type === 'download' && (
      <path
        fill="#2EC4B6"
        d="M17.75 14.775c.414 0 .75.336.75.75v3.726c0 .414-.336.75-.75.75h-16c-.414 0-.75-.336-.75-.75v-3.726c0-.414.336-.75.75-.75s.75.336.75.75V18.5H17v-2.976c0-.414.336-.75.75-.75zM9.775 0c.411 0 .746.332.75.744.021 2.7.076 10.358.035 13.56l3.913-3.803c.298-.288.772-.281 1.061.016.29.297.282.771-.015 1.06l-5.247 5.099c-.145.142-.334.212-.522.212-.189 0-.377-.07-.523-.212l-5.246-5.099c-.296-.289-.303-.763-.014-1.06.29-.297.763-.304 1.06-.016l4.035 3.923c.031-2.554 0-9.099-.037-13.668-.004-.414.33-.753.744-.756z"
      />
    )}
    {type === 'big-plus' && (
      <path
        fill="#2EC4B6"
        d="M10 .25c.37 0 .677.275.725.632l.007.1-.001 8.536h8.537c.404 0 .732.328.732.732 0 .37-.275.677-.632.725l-.1.007-8.537-.001v8.537c0 .404-.327.732-.731.732-.37 0-.677-.275-.725-.632l-.007-.1v-8.537H.732c-.404 0-.732-.327-.732-.731 0-.37.275-.677.632-.725l.1-.007h8.536V.982c0-.404.328-.732.732-.732z"
      />
    )}
    {type === 'big-minus' && (
      <path
        fill="#2EC4B6"
        d="M19.268 9c.404 0 .732.328.732.732 0 .37-.275.676-.632.725l-.1.006H.732c-.404 0-.732-.327-.732-.731 0-.37.275-.677.632-.725L.732 9h18.536z"
      />
    )}
    {type === 'clock' && (
      <path
        fill="#4C4C4C"
        d="M10.25 0C15.911 0 20.5 4.589 20.5 10.25S15.911 20.5 10.25 20.5 0 15.911 0 10.25 4.589 0 10.25 0zm0 1.5c-4.833 0-8.75 3.917-8.75 8.75S5.417 19 10.25 19 19 15.083 19 10.25 15.083 1.5 10.25 1.5zm0 2.458c.38 0 .693.282.743.648l.007.102V9.5h4c.38 0 .693.282.743.648l.007.102c0 .38-.282.693-.648.743L15 11h-4.75c-.38 0-.693-.282-.743-.648L9.5 10.25V4.708c0-.414.336-.75.75-.75z"
      />
    )}
    {type === 'hamburger' && (
      <path
        fill="#2EC4B6"
        d="M19.3023256,17.4545455 C19.6876405,17.4545455 20,17.8005072 20,18.2272727 C20,18.6184744 19.7375313,18.9417786 19.3969959,18.9929459 L19.3023256,19 L0.697674419,19 C0.312359477,19 -2.13162821e-14,18.6540382 -2.13162821e-14,18.2272727 C-2.13162821e-14,17.836071 0.262468727,17.5127669 0.603004133,17.4615995 L0.697674419,17.4545455 L19.3023256,17.4545455 Z M19.3023256,9.72727273 C19.6876405,9.72727273 20,10.0732345 20,10.5 C20,10.8912017 19.7375313,11.2145058 19.3969959,11.2656732 L19.3023256,11.2727273 L0.697674419,11.2727273 C0.312359477,11.2727273 -2.13162821e-14,10.9267655 -2.13162821e-14,10.5 C-2.13162821e-14,10.1087983 0.262468727,9.78549416 0.603004133,9.73432682 L0.697674419,9.72727273 L19.3023256,9.72727273 Z M19.3023256,2 C19.6876405,2 20,2.34596178 20,2.77272727 C20,3.16392897 19.7375313,3.48723311 19.3969959,3.53840046 L19.3023256,3.54545455 L0.697674419,3.54545455 C0.312359477,3.54545455 -2.13162821e-14,3.19949276 -2.13162821e-14,2.77272727 C-2.13162821e-14,2.38152557 0.262468727,2.05822143 0.603004133,2.00705409 L0.697674419,2 L19.3023256,2 Z"
      />
    )}
    {type === 'world' && (
      <path
          fill="#2EC4B6"
          d="M10 0c5.523 0 10 4.477 10 10s-4.477 10-10 10S0 15.523 0 10 4.477 0 10 0zM6.84 15.086l.023.062c.602 1.701 1.48 2.909 2.44 3.31v-3.122c-.854-.03-1.679-.115-2.462-.25zm6.319 0l-.186.03c-.728.118-1.49.193-2.275.22v3.123c.97-.407 1.858-1.64 2.46-3.373zM2.047 13.29l.048.116c.886 2.053 2.548 3.692 4.616 4.549-.6-.865-1.092-1.964-1.444-3.22-1.257-.353-2.356-.845-3.22-1.445zm15.907 0l-.038.025c-.858.589-1.943 1.072-3.183 1.419-.352 1.257-.844 2.356-1.444 3.22 2.107-.872 3.792-2.557 4.665-4.664zm-8.652-2.592H6.06c.04 1.025.166 1.996.36 2.883.887.194 1.858.32 2.882.36v-3.243zm4.638 0h-3.243v3.243c1.025-.04 1.996-.166 2.883-.36.194-.887.32-1.858.36-2.882zm-9.276 0H1.54c.407.972 1.64 1.86 3.373 2.462-.135-.783-.22-1.608-.25-2.461zm13.795 0h-3.123c-.03.854-.115 1.679-.25 2.462 1.734-.603 2.966-1.49 3.373-2.461zM10.698 6.06l-.001 3.242h3.243c-.04-1.024-.166-1.995-.36-2.882-.887-.194-1.858-.32-2.882-.36zm4.388.781l.03.186c.118.728.193 1.49.22 2.275h3.123c-.407-.97-1.64-1.858-3.373-2.46zM9.302 6.06c-1.024.04-1.995.166-2.882.36-.194.887-.32 1.858-.36 2.882h3.242zm-4.388.78l-.062.023c-1.701.602-2.909 1.48-3.31 2.44h3.122c.03-.854.115-1.679.25-2.462zm1.797-4.793l-.116.048c-2.053.886-3.692 2.548-4.549 4.616.865-.6 1.964-1.092 3.22-1.444.353-1.257.845-2.356 1.445-3.22zm6.578 0l.025.037c.589.858 1.072 1.943 1.419 3.183 1.257.352 2.356.844 3.22 1.444-.872-2.107-2.557-3.792-4.664-4.665zM9.302 1.54c-.97.407-1.858 1.64-2.46 3.373.782-.135 1.607-.22 2.46-.25zm1.396 0v3.123c.853.03 1.678.115 2.461.25-.603-1.734-1.49-2.966-2.461-3.373z"/>
    )}
    {type === 'filter' && (
      <svg fill="none">
        <path
          d="M12.6918 16.5412C12.1842 16.5412 11.8459 16.216 11.8459 15.7282V9.14222C11.8459 8.89829 11.9305 8.73568 12.0997 8.57306L19.1205 1.90582H2.87945L9.90034 8.57306C10.0695 8.73568 10.1541 8.89829 10.1541 9.14222V13.6955C10.1541 14.1833 9.81575 14.5085 9.30822 14.5085C8.80069 14.5085 8.46233 14.1833 8.46233 13.6955V9.46745L0.257194 1.6619C0.00342739 1.41797 -0.0811616 1.09274 0.0880164 0.76751C0.257194 0.442279 0.510962 0.279663 0.849318 0.279663H21.1507C21.489 0.279663 21.8274 0.442279 21.912 0.76751C22.0812 1.09274 21.9966 1.41797 21.7428 1.6619L13.5377 9.46745V15.7282C13.5377 16.216 13.1993 16.5412 12.6918 16.5412Z"
          fill="#2EC4B6"
        />
      </svg>
    )}
  </StyledIcon>
);

Icon.propTypes = {
  backgroundColor: PropTypes.string,
  foregroundColor: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  type: PropTypes.oneOf([
    'calendar',
    'close',
    'complete',
    'complete-unfilled',
    'incomplete',
    'down-caret',
    'left-caret',
    'right-caret',
    'error',
    'success',
    'info',
    'warning',
    'checkbox-filled',
    'checkbox-unfilled',
    'checkbox-simple',
    'add',
    'pencil',
    'hide',
    'show',
    'search',
    'fax',
    'printer',
    'envelope',
    'download',
    'big-plus',
    'big-minus',
    'clock',
    'hamburger',
    'world',
    'filter',
  ]).isRequired,
};

Icon.defaultProps = {
  backgroundColor: null,
  foregroundColor: null,
  size: 'medium',
};

export default Icon;
